/**
 * Achievements (owner decision 30.09, variant A): catalog, counter modes, the signed award payload and DTOs.
 * Spec: docs/superpowers/specs/2026-09-30-achievements-share-design.md.
 *
 * A tier is `{ medal, key, n }`: awarded once the per-user counter `key` reaches `n` (tiers of one achievement may
 * use different keys — jackpot Mini/Major/Grand). Thresholds are numbers we choose → no digit 4.
 * Awards give NO tokens. The server evaluates everything from settled bets and social events (after commit).
 */
import { z } from 'zod';
import type { GameId } from './common';
import type { SeasonId } from './season';

export const MEDALS = ['bronze', 'silver', 'gold'] as const;
export type Medal = (typeof MEDALS)[number];

export const ACH_GROUPS = ['start', 'streak', 'level', 'social', 'season', 'tourney', 'rare'] as const;
export type AchGroup = (typeof ACH_GROUPS)[number];

/** Games announced before they are merged; their achievements stay inactive until the id is in GAME_IDS. */
export const PENDING_GAMES = [] as const;
export type AchGame = GameId | (typeof PENDING_GAMES)[number];

export const ACHIEVEMENT_IDS = [
  'first-round', 'first-win', 'explorer', 'traveller', 'regular', 'win-streak', 'daily-week', 'level', 'friends', 'inviter',
  'spring-festival', 'lantern', 'dragon-boat', 'mid-autumn', 'podium', 'big-win', 'liftoff-orbit', 'roulette-green',
  'pot-winner', 'sweet-jackpot', 'koi-88', 'workshop-master', 'carp-gate', 'firecrackers-clear', 'hongbao-luckiest',
  'mahjong-hand', 'zhajinhua-leopard',
] as const;
export type AchievementId = (typeof ACHIEVEMENT_IDS)[number];

export interface AchievementTier {
  medal: Medal;
  key: string;
  n: number;
}

export interface AchievementDef {
  id: AchievementId;
  group: AchGroup;
  game: AchGame | null;
  tiers: readonly AchievementTier[];
  /** holiday: one award per year (tier 1, value = year) while the season is active; not a counter */
  yearly?: SeasonId;
  /** rare event: shown as "???" without progress until earned */
  secret?: boolean;
}

const t = (medal: Medal, key: string, n: number): AchievementTier => ({ medal, key, n });
const three = (key: string, a: number, b: number, c: number): AchievementTier[] => [t('bronze', key, a), t('silver', key, b), t('gold', key, c)];
const holiday = (id: SeasonId & AchievementId): AchievementDef => ({ id, group: 'season', game: null, yearly: id, tiers: [t('gold', `season:${id}`, 1)] });

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  { id: 'first-round', group: 'start', game: null, tiers: [t('bronze', 'rounds', 1)] },
  { id: 'first-win', group: 'start', game: null, tiers: [t('bronze', 'wins', 1)] },
  { id: 'explorer', group: 'start', game: null, tiers: three('games', 3, 6, 10) },
  // Game Passport (passport.ts): 5 and 10 stamps, gold — every game of the catalog at the time (counter 0/1)
  { id: 'traveller', group: 'start', game: null, tiers: [t('bronze', 'passport', 5), t('silver', 'passport', 10), t('gold', 'passport:full', 1)] },
  { id: 'regular', group: 'start', game: null, tiers: three('rounds', 100, 1000, 10000) },
  { id: 'win-streak', group: 'streak', game: null, tiers: three('streak', 3, 5, 8) },
  { id: 'daily-week', group: 'streak', game: null, tiers: three('daily', 7, 30, 100) },
  {
    id: 'level',
    group: 'level',
    game: null,
    tiers: [
      ...[10, 20, 30].map((n) => t('bronze', 'level', n)),
      ...[50, 80].map((n) => t('silver', 'level', n)),
      ...[100, 150, 200].map((n) => t('gold', 'level', n)),
    ],
  },
  { id: 'friends', group: 'social', game: null, tiers: three('friends', 1, 5, 20) },
  { id: 'inviter', group: 'social', game: null, tiers: three('invites', 1, 3, 8) },
  holiday('spring-festival'),
  holiday('lantern'),
  holiday('dragon-boat'),
  holiday('mid-autumn'),
  { id: 'podium', group: 'tourney', game: null, tiers: [t('bronze', 'place:3', 1), t('silver', 'place:2', 1), t('gold', 'place:1', 1)] },
  { id: 'big-win', group: 'rare', game: null, tiers: three('x', 5000, 20000, 100000) },
  { id: 'liftoff-orbit', group: 'rare', game: 'crash', tiers: three('crash:x', 1000, 10000, 100000) },
  { id: 'roulette-green', group: 'rare', game: 'roulette', tiers: three('roulette:green', 1, 8, 28) },
  { id: 'pot-winner', group: 'rare', game: 'pot', tiers: [t('bronze', 'pot:win', 1), t('silver', 'pot:x5', 1), t('gold', 'pot:x20', 1)] },
  { id: 'sweet-jackpot', group: 'rare', game: 'sweet-shop', tiers: [t('bronze', 'sweet:mini', 1), t('silver', 'sweet:major', 1), t('gold', 'sweet:grand', 1)] },
  { id: 'koi-88', group: 'rare', game: 'koi-pond', secret: true, tiers: [t('gold', 'koi:88', 1)] },
  { id: 'workshop-master', group: 'rare', game: 'workshop', tiers: three('workshop:stage3', 1, 3, 8) },
  { id: 'carp-gate', group: 'rare', game: 'carp', tiers: [t('bronze', 'carp:easy', 1), t('silver', 'carp:medium', 1), t('gold', 'carp:hard', 1)] },
  { id: 'firecrackers-clear', group: 'rare', game: 'firecrackers', tiers: three('fc:clear', 1, 3, 8) },
  { id: 'hongbao-luckiest', group: 'rare', game: 'hongbao', tiers: three('hongbao:luckiest', 1, 8, 28) },
  {
    id: 'mahjong-hand',
    group: 'rare',
    game: 'mahjong',
    tiers: [t('bronze', 'mj:fullFlush', 1), t('silver', 'mj:allHonors', 1), t('gold', 'mj:thirteenOrphans', 1)],
  },
  { id: 'zhajinhua-leopard', group: 'rare', game: 'zhajinhua', secret: true, tiers: [t('gold', 'zjh:leopard', 1)] },
];

export type CounterMode = 'add' | 'max' | 'set';
const MAX_KEYS = new Set(['x', 'crash:x', 'streak', 'level', 'daily', 'passport:full']);
const SET_KEYS = new Set(['games', 'friends', 'invites', 'passport']);
/** add — sum of facts; max — best value; set — recomputed by the server (a count query) */
export function counterMode(key: string): CounterMode {
  return MAX_KEYS.has(key) ? 'max' : SET_KEYS.has(key) ? 'set' : 'add';
}

/* ---------------- signed payload ---------------- */

export type CanonValue = string | number | null | readonly CanonValue[] | { readonly [k: string]: CanonValue };

/**
 * RFC 8785 (JCS) for the subset we sign: strings, safe integers, null, arrays, objects. Keys sorted by UTF-16 code
 * units (= JCS for our ASCII keys), no whitespace. Fractions are rejected on purpose (multipliers are hundredths).
 */
export function canonicalJson(v: CanonValue): string {
  if (v === null) return 'null';
  if (typeof v === 'string') return JSON.stringify(v);
  if (typeof v === 'number') {
    if (!Number.isSafeInteger(v)) throw new Error(`canonicalJson: ${v} is not a safe integer`);
    return String(v);
  }
  if (Array.isArray(v)) return `[${(v as readonly CanonValue[]).map(canonicalJson).join(',')}]`;
  const o = v as { readonly [k: string]: CanonValue };
  return `{${Object.keys(o)
    .sort()
    .map((k) => {
      const x = o[k];
      if (x === undefined) throw new Error(`canonicalJson: ${k} is undefined`);
      return `${JSON.stringify(k)}:${canonicalJson(x)}`;
    })
    .join(',')}}`;
}

/** 12 characters of [A-Z2-9] without I/O/0/1 — 60 random bits, not enumerable */
export const AWARD_ID_RE = /^[A-HJ-NP-Z2-9]{12}$/;
/**
 * `holder` — the player's pseudonymous id in the signed payload (owner decision 30.09): 16 random bytes as 32 lowercase
 * hex, generated once per player (server: `randomBytes(HOLDER_BYTES).toString('hex')`), NOT derived from publicId or
 * the numeric user id. Signed copies held by third parties therefore do not reveal the player's number, even with
 * `showName = false`. Never shown in the UI besides the downloadable proof.
 */
export const HOLDER_BYTES = 16;
export const HOLDER_RE = /^[0-9a-f]{32}$/;
export const MOMENT_WIN = 'win';
export const MOMENT_PLACE = 'place';

export const zAwardPayload = z
  .object({
    v: z.literal(1),
    iss: z.string().regex(/^[a-z0-9.-]{2,64}$/),
    kid: z.string().regex(/^[0-9a-f]{16}$/),
    id: z.string().regex(AWARD_ID_RE),
    holder: z.string().regex(HOLDER_RE),
    ach: z.string().regex(/^[a-z0-9-]{2,32}$/),
    tier: z.number().int().min(0).max(99),
    value: z.number().int().min(0).max(2_000_000_000),
    game: z.string().regex(/^[a-z0-9-]{2,32}$/).nullable(),
    at: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/),
  })
  .strict();
export type AwardPayload = z.infer<typeof zAwardPayload>;

export const awardAt = (d: Date): string => `${d.toISOString().slice(0, 19)}Z`;

/**
 * Cross-implementation vector: key = RFC 8032 §7.1 TEST 1 (seed 9d61b1…7f60, public d75a98…511a).
 * kid = first 16 hex of sha256(publicKeyRaw). Signed with node:crypto; the web verifies it with @noble/ed25519.
 */
export const AWARD_TEST_VECTOR = {
  seed: 'nWGxne_9WmC6hEr0kuwsxERJxWl7MmkZcDusAxyuf2A',
  publicKey: '11qYAYKxCrfVS_7TyWQHOg7hcvPapiMlrwIaaPcHURo',
  kid: '21fe31dfa154a261',
  payload: {
    v: 1,
    iss: 'token-games',
    kid: '21fe31dfa154a261',
    id: 'ABCDEFGHJKLM',
    holder: '7c3e9a51d2f08b6e1a5c9d3f7b2e8065',
    ach: 'carp-gate',
    tier: 3,
    value: 1,
    game: 'carp',
    at: '2026-09-30T12:00:00Z',
  },
  canonical:
    '{"ach":"carp-gate","at":"2026-09-30T12:00:00Z","game":"carp","holder":"7c3e9a51d2f08b6e1a5c9d3f7b2e8065","id":"ABCDEFGHJKLM","iss":"token-games","kid":"21fe31dfa154a261","tier":3,"v":1,"value":1}',
  sig: 'COM24tZgvz9o-ga19azOB8QWxyGQPbh_p7UrRY7pvYPb1mj8rGRUxDVvbDFSqfq8lcwbXPf2Vg1nsg8ueB0hAQ',
} as const;

/* ---------------- DTOs ---------------- */

export type AwardKind = 'achievement' | 'moment';

export interface AwardView {
  id: string;
  kind: AwardKind;
  /** achievement id | 'win' | 'place' */
  ach: string;
  tier: number;
  medal: Medal | null;
  /** threshold / year / place / multiplier in hundredths (moments 'win') */
  value: number;
  game: string | null;
  /** tournament title (moment 'place') */
  title: string | null;
  awardedAt: string;
  shared: boolean;
}

export interface AwardProof {
  alg: 'Ed25519';
  kid: string;
  /** canonical JSON — exactly the signed bytes */
  payload: string;
  /** base64url */
  sig: string;
}

export type PublicAwardBrief = Pick<AwardView, 'id' | 'ach' | 'tier' | 'medal' | 'value' | 'game'>;

/** GET /api/public/awards/:id — no balance, no e-mail, no token amounts */
export interface PublicAward extends Omit<AwardView, 'shared'> {
  owner: { nickname: string; avatar: string } | null;
  /** owner's invite code for "Play for free" (null when the owner hides public pages) */
  invite: string | null;
  proof: AwardProof;
  cards: { og: string; sq: string };
}

export interface MyAchievementTier {
  medal: Medal;
  n: number;
  award: { id: string; at: string; shared: boolean } | null;
}

export interface MyAchievement {
  id: AchievementId;
  group: AchGroup;
  game: AchGame | null;
  secret: boolean;
  /** current counter of the next tier's key; null for secret ones not yet earned and for holidays */
  progress: number | null;
  tiers: MyAchievementTier[];
  /** holidays: one entry per year earned */
  years: Array<{ year: number; award: { id: string; at: string; shared: boolean } }>;
}

export interface PublicPrivacy {
  /** every public page of mine → 404 */
  hidden: boolean;
  /** nickname and avatar on my public pages */
  showName: boolean;
}

/** GET /api/me/achievements */
export interface AchievementsResponse {
  achievements: MyAchievement[];
  /** moments (big wins, tournament places), newest first, ≤ 50 */
  moments: AwardView[];
  privacy: PublicPrivacy;
  /** ids of awards the player has not seen yet (the «Profile» menu badge); optional — an older server leaves it out */
  unseen?: string[];
}

/** most award ids one POST /api/me/achievements/seen may name */
export const ACH_SEEN_MAX_IDS = 500;
/** POST /api/me/achievements/seen: mark these awards (all of them without `ids`) as seen */
export const zAchievementsSeen = z.object({ ids: z.array(z.string().min(1).max(64)).max(ACH_SEEN_MAX_IDS).optional() }).strict();
export type AchievementsSeen = z.infer<typeof zAchievementsSeen>;

export const zPrivacyUpdate = z.object({ hidden: z.boolean().optional(), showName: z.boolean().optional() }).strict();
export type PrivacyUpdate = z.infer<typeof zPrivacyUpdate>;

/** GET /.well-known/tg-achievement-keys.json */
export interface KeyDocument {
  issuer: string;
  keys: Array<{ kid: string; alg: 'Ed25519'; x: string; status: 'active' | 'retired' }>;
  revoked: string[];
}

export interface AwardedEvent {
  awardId: string;
  kind: AwardKind;
  ach: string;
  tier: number;
  medal: Medal | null;
  value: number;
  game: string | null;
}
export interface ShareEligibleEvent {
  betId: string;
  game: GameId;
  /** multiplier in hundredths (12850 = ×128.5) */
  x: number;
}

export interface AchievementServerToClient {
  /** a new award for this player (toast "Share") */
  'achievement:awarded': (e: AwardedEvent) => void;
  /** a settled win ≥ ×50 may be shared as a moment (POST /api/share/moments) */
  'share:eligible': (e: ShareEligibleEvent) => void;
}
